import { RfSuiteHeader } from './RfSuiteHeader.js';
import { getLanguage, t, localizedPath } from '../public/i18n.js';
import { useEffect, useMemo, useState } from 'react';

type IconName =
  | 'arrow'
  | 'bell'
  | 'check'
  | 'chevron'
  | 'chip'
  | 'clock'
  | 'device'
  | 'document'
  | 'eye'
  | 'globe'
  | 'grid'
  | 'lock'
  | 'network'
  | 'printer'
  | 'radar'
  | 'shield'
  | 'signal'
  | 'stop'
  | 'wifi';

type Scenario = {
  id: string;
  number: string;
  title: string;
  description: string;
  evidence: string;
  duration: string;
  icon: IconName;
  availability: 'Prototype ready' | 'Agent integration' | 'Planned';
};

type DeviceRow = {
  name: string;
  detail: string;
  manufacturer: string;
  status: 'Trusted' | 'Watch' | 'Guest' | 'Unknown';
  lastSeen: string;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const scenarios: Scenario[] = [
  {
    id: 'printer',
    number: '01',
    title: 'Rogue printer demonstration',
    description:
      'Advertise one temporary assessment printer, accept the first client-approved job, and close the capture window immediately.',
    evidence: 'One job · local encrypted evidence · client-controlled deletion',
    duration: '2 minutes',
    icon: 'printer',
    availability: 'Agent integration',
  },
  {
    id: 'ssid',
    number: '02',
    title: 'Workplace look-alike SSID',
    description:
      'Measure whether a user voluntarily joins an authorized look-alike network without deauthentication or forced roaming.',
    evidence: 'Association behavior · captive-portal outcome · no Wi-Fi key capture',
    duration: '2 minutes',
    icon: 'wifi',
    availability: 'Agent integration',
  },
  {
    id: 'credential',
    number: '03',
    title: 'Credential-entry behavior',
    description:
      'Present a client-approved generic login prompt and record only whether fields were populated and submitted.',
    evidence: 'Boolean-only evidence · typed values never leave the browser',
    duration: '2 minutes',
    icon: 'lock',
    availability: 'Prototype ready',
  },
  {
    id: 'traffic',
    number: '04',
    title: 'Traffic intelligence profile',
    description:
      'Summarize visible DNS, HTTP and TLS destination metadata and identify likely business-critical login services.',
    evidence: 'Metadata-first · payload retention disabled by default',
    duration: 'Scoped window',
    icon: 'radar',
    availability: 'Planned',
  },
];

const devices: DeviceRow[] = [
  {
    name: 'Reception printer',
    detail: '192.168.10.24 · Ethernet',
    manufacturer: 'Brother Industries',
    status: 'Trusted',
    lastSeen: 'Now',
  },
  {
    name: 'Conference TV',
    detail: '192.168.10.41 · 5 GHz',
    manufacturer: 'Samsung Electronics',
    status: 'Trusted',
    lastSeen: 'Now',
  },
  {
    name: 'Former guest phone',
    detail: '192.168.10.87 · Private MAC',
    manufacturer: 'Manufacturer hidden',
    status: 'Watch',
    lastSeen: '1 min ago',
  },
  {
    name: 'Unidentified device',
    detail: '192.168.10.116 · 2.4 GHz',
    manufacturer: 'Espressif Systems',
    status: 'Unknown',
    lastSeen: 'Just joined',
  },
];

const monitoringFeatures = [
  {
    icon: 'bell' as IconName,
    title: 'Immediate join alerts',
    text: 'Receive Android push alerts or, while the web console is open, browser notifications when a new, watched or guest device starts a fresh network session.',
  },
  {
    icon: 'device' as IconName,
    title: 'Identity with evidence',
    text: 'Combine hostnames, advertised services, IEEE manufacturer assignments and user labels without overstating uncertain matches.',
  },
  {
    icon: 'network' as IconName,
    title: 'Network history',
    text: 'Track first seen, last seen, online sessions, IP changes and the evidence source behind every observation.',
  },
  {
    icon: 'shield' as IconName,
    title: 'Coverage you can trust',
    text: 'Clearly show which network segments the appliance can observe and when router integration or an additional sensor is required.',
  },
];

function Icon({ name, size = 20 }: { name: IconName; size?: number }): JSX.Element {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  switch (name) {
    case 'arrow':
      return (
        <svg {...common}>
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      );
    case 'bell':
      return (
        <svg {...common}>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
          <path d="M10 21h4" />
        </svg>
      );
    case 'check':
      return (
        <svg {...common}>
          <path d="m5 12 4 4L19 6" />
        </svg>
      );
    case 'chevron':
      return (
        <svg {...common}>
          <path d="m9 18 6-6-6-6" />
        </svg>
      );
    case 'chip':
      return (
        <svg {...common}>
          <rect x="6" y="6" width="12" height="12" rx="2" />
          <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3M9 10h6v4H9z" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'device':
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="13" rx="2" />
          <path d="M8 21h8M12 16v5" />
        </svg>
      );
    case 'document':
      return (
        <svg {...common}>
          <path d="M6 2h8l4 4v16H6zM14 2v5h5M9 12h6M9 16h6" />
        </svg>
      );
    case 'eye':
      return (
        <svg {...common}>
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
          <circle cx="12" cy="12" r="2.5" />
        </svg>
      );
    case 'globe':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </svg>
      );
    case 'grid':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...common}>
          <rect x="5" y="10" width="14" height="11" rx="3" />
          <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10M12 14v3" />
        </svg>
      );
    case 'network':
      return (
        <svg {...common}>
          <rect x="9" y="2" width="6" height="5" rx="1.5" />
          <rect x="2" y="17" width="6" height="5" rx="1.5" />
          <rect x="16" y="17" width="6" height="5" rx="1.5" />
          <path d="M12 7v5M5 17v-3h14v3" />
        </svg>
      );
    case 'printer':
      return (
        <svg {...common}>
          <path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
          <path d="M6 14h12v8H6zM18 12h.01" />
        </svg>
      );
    case 'radar':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
          <path d="M12 12 18.5 5.5M12 3v2M3 12h2M12 19v2M19 12h2" />
          <circle cx="16" cy="8" r="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...common}>
          <path d="M12 2 20 5v6c0 5.4-3.2 9.3-8 11-4.8-1.7-8-5.6-8-11V5l8-3Z" />
          <path d="m8.5 12 2.2 2.2 4.8-5" />
        </svg>
      );
    case 'signal':
      return (
        <svg {...common}>
          <path d="M4 20v-4M9.3 20v-8M14.7 20V8M20 20V4" />
        </svg>
      );
    case 'stop':
      return (
        <svg {...common}>
          <rect x="5" y="5" width="14" height="14" rx="2" />
        </svg>
      );
    case 'wifi':
      return (
        <svg {...common}>
          <path d="M2 8.8a16 16 0 0 1 20 0M5 12.5a11 11 0 0 1 14 0M8.5 16a5.5 5.5 0 0 1 7 0" />
          <circle cx="12" cy="20" r="1" fill="currentColor" stroke="none" />
        </svg>
      );
  }
}

function StatusBadge({ status }: { status: DeviceRow['status'] }): JSX.Element {
  return <span className={`device-status status-${status.toLowerCase()}`}>{t(status)}</span>;
}

export function App(): JSX.Element {
  const [, updateLanguage] = useState(getLanguage);
  useEffect(() => { const update = (): void => updateLanguage(getLanguage()); window.addEventListener('sentinel-language-change', update); return () => window.removeEventListener('sentinel-language-change', update); }, []);
  const [activeScenario, setActiveScenario] = useState<Scenario | null>(null);
  const [seconds, setSeconds] = useState(120);
  const [apiState, setApiState] = useState<'checking' | 'online' | 'offline'>('checking');
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  const progress = useMemo(
    () => Math.max(0, Math.min(100, ((120 - seconds) / 120) * 100)),
    [seconds],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4500);

    void fetch('/health', { signal: controller.signal, headers: { Accept: 'application/json' } })
      .then((response) => {
        setApiState(response.ok ? 'online' : 'offline');
      })
      .catch(() => setApiState('offline'))
      .finally(() => window.clearTimeout(timeout));

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const handler = (event: Event): void => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (!activeScenario) return;

    const timer = window.setInterval(() => {
      setSeconds((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          setActiveScenario(null);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [activeScenario]);

  function armScenario(scenario: Scenario): void {
    setActiveScenario(scenario);
    setSeconds(120);
    window.setTimeout(() => {
      document.querySelector('#operator-console')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }

  function stopScenario(): void {
    setActiveScenario(null);
    setSeconds(120);
  }

  async function installApp(): Promise<void> {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }


  return (
    <div className="site-frame">
      <a className="skip-link" href="#main-content">{t("Skip to content")}</a>

      <RfSuiteHeader />

      <main id="main-content">
        <nav className="section-nav shell" aria-label={t('Network Sentinel on this page')}><strong>Network Sentinel</strong><a href="#monitoring">{t('Monitoring')}</a><a href="#assessment">{t('Rapid assessment')}</a><a href="#appliance">{t('Appliance')}</a><a href="#safety">{t('Safety')}</a><a href="https://autosecurelogin.com/#contact">{t('Request an assessment')}</a></nav>
        <section className="hero" id="top">
          <div className="hero-noise" aria-hidden="true" />
          <div className="hero-splash" aria-hidden="true"><img src="/assets/art/asl-platform-network.webp" width={1200} height={900} alt="" decoding="async" /></div>
          <div className="hero-grid shell">
            <div className="hero-copy reveal">
              <div className="hero-kicker">
                <span className="live-dot" />{t("Network visibility + authorized security validation")}</div>
              <p className="hero-store"><a className="store-link" href="https://play.google.com/store/apps/details?id=com.autosecurelogin.networksentinel">{t("Get it on Google Play")} <span className="store-price">{t("$9.99 one time")}</span></a></p>
              <h1>{t("Know what joined.")}<span>{t("Prove what could be exploited.")}</span>
              </h1>
              <p>{t("Sentinel combines continuous device monitoring with a Raspberry Pi 5 rapid-assessment prototype to help make network risk understandable.")}</p>
              <div className="hero-actions">
                <a className="button button-primary button-large" href="#assessment">{t("Explore rapid assessment")}<Icon name="arrow" size={18} />
                </a>
                <a className="button button-ghost button-large" href="#monitoring">{t("See network monitoring")}</a>
              </div>
              <div className="hero-proof" aria-label={t("Sentinel platform highlights")}>
                <span><Icon name="check" size={16} />{t("New-device push alerts")}</span>
                <span><Icon name="check" size={16} />{t("Manufacturer matching")}</span>
                <span><Icon name="check" size={16} />{t("Signed assessment scope")}</span>
              </div>
            </div>

            <div className="network-visual reveal" aria-label={t("Illustrative Sentinel network map")}>
              <div className="visual-chrome">
                <div className="window-dots" aria-hidden="true"><span /><span /><span /></div>
                <span>{t("SENTINEL / ILLUSTRATIVE NETWORK")}</span>
                <span className="visual-status"><i />{t("Observing")}</span>
              </div>
              <div className="visual-canvas">
                <div className="map-grid" aria-hidden="true" />
                <div className="map-line line-one" aria-hidden="true" />
                <div className="map-line line-two" aria-hidden="true" />
                <div className="map-line line-three" aria-hidden="true" />
                <div className="gateway-node">
                  <span><Icon name="shield" size={29} /></span>
                  <strong>Sentinel</strong>
                  <small>{t("Gateway observer")}</small>
                </div>
                <div className="device-node node-phone">
                  <span><Icon name="device" size={17} /></span>
                  <div><strong>{t("Hunter’s phone")}</strong><small>{t("Trusted")}</small></div>
                </div>
                <div className="device-node node-printer">
                  <span><Icon name="printer" size={17} /></span>
                  <div><strong>{t("Office printer")}</strong><small>Brother Industries</small></div>
                </div>
                <div className="device-node node-unknown">
                  <span><Icon name="wifi" size={17} /></span>
                  <div><strong>{t("New device")}</strong><small>{t("Just joined")}</small></div>
                </div>
                <div className="device-node node-laptop">
                  <span><Icon name="device" size={17} /></span>
                  <div><strong>{t("Workstation 07")}</strong><small>{t("Watched")}</small></div>
                </div>

                <div className="join-alert">
                  <div className="alert-icon"><Icon name="bell" size={18} /></div>
                  <div>
                    <span>{t("NEW DEVICE DETECTED")}</span>
                    <strong>{t("Espressif device joined Staff Wi-Fi")}</strong>
                    <small>{t("192.168.10.116 · 8 seconds ago")}</small>
                  </div>
                  <button type="button" aria-label={t("Open illustrative alert")}><Icon name="chevron" size={17} /></button>
                </div>
              </div>
              <div className="visual-footer">
                <span><strong>24</strong>{t("known")}</span>
                <span><strong>18</strong>{t("online")}</span>
                <span className="attention"><strong>1</strong>{t("unreviewed")}</span>
                <span><strong>100%</strong>{t("agent health")}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="signal-strip" aria-label={t("Sentinel deployment principles")}>
          <div className="shell signal-grid">
            <div><Icon name="chip" /><span><strong>{t("Raspberry Pi 5 first")}</strong>{t("Native Linux agent")}</span></div>
            <div><Icon name="globe" /><span><strong>{t("Android + PWA")}</strong>{t("One responsive console")}</span></div>
            <div><Icon name="shield" /><span><strong>{t("Evidence controlled")}</strong>{t("Local by default")}</span></div>
            <div><Icon name="clock" /><span><strong>{t("Fast deployment")}</strong>{t("Minimal client setup")}</span></div>
          </div>
        </section>

        <section className="section section-light" id="monitoring">
          <div className="shell">
            <div className="section-heading split-heading">
              <div>
                <p className="eyebrow eyebrow-dark">{t("Continuous network watch")}</p>
                <h2>{t("Every visible device, explained and remembered.")}</h2>
              </div>
              <p>{t("The always-on agent builds a durable inventory, identifies devices from multiple signals and opens a new session only when a device genuinely returns—so alerts remain useful instead of noisy.")}</p>
            </div>

            <div className="monitor-layout">
              <div className="feature-grid">
                {monitoringFeatures.map((feature) => (
                  <article className="feature-card" key={feature.title}>
                    <span className="feature-icon"><Icon name={feature.icon} /></span>
                    <h3>{t(feature.title)}</h3>
                    <p>{t(feature.text)}</p>
                  </article>
                ))}
              </div>

              <div className="inventory-window">
                <div className="inventory-header">
                  <div>
                    <span className="mini-label">{t("DEVICE INVENTORY")}</span>
                    <strong>{t("Staff network")}</strong>
                  </div>
                  <div className="inventory-actions">
                    <span><i />{t("Live")}</span>
                    <button type="button" aria-label={t("Inventory display options")}><Icon name="grid" size={17} /></button>
                  </div>
                </div>
                <div className="inventory-summary">
                  <div><span>{t("Observed")}</span><strong>27</strong></div>
                  <div><span>{t("Online now")}</span><strong>18</strong></div>
                  <div><span>{t("Watched")}</span><strong>3</strong></div>
                  <div className="summary-alert"><span>{t("Unreviewed")}</span><strong>1</strong></div>
                </div>
                <div className="device-table" role="table" aria-label={t("Illustrative device inventory")}>
                  <div className="device-table-head" role="row">
                    <span role="columnheader">{t("Device")}</span>
                    <span role="columnheader">{t("Manufacturer")}</span>
                    <span role="columnheader">{t("Policy")}</span>
                    <span role="columnheader">{t("Seen")}</span>
                  </div>
                  {devices.map((device) => (
                    <div className="device-row" role="row" key={device.name}>
                      <span className="device-name" role="cell">
                        <i className={device.status === 'Unknown' ? 'device-avatar unknown' : 'device-avatar'}>
                          <Icon name={device.name.includes('printer') ? 'printer' : 'device'} size={17} />
                        </i>
                        <span><strong>{t(device.name)}</strong><small>{t(device.detail)}</small></span>
                      </span>
                      <span role="cell">{device.manufacturer}</span>
                      <span role="cell"><StatusBadge status={device.status} /></span>
                      <span role="cell">{t(device.lastSeen)}</span>
                    </div>
                  ))}
                </div>
                <div className="inventory-note">
                  <Icon name="eye" size={17} />{t("Illustrative data. Production results show the evidence source and confidence behind each identity.")}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-dark assessment-section" id="assessment">
          <div className="assessment-glow" aria-hidden="true" />
          <div className="shell">
            <div className="section-heading split-heading heading-on-dark">
              <div>
                <p className="eyebrow">{t("Authorized rapid assessment")}</p>
                <h2>{t("Turn an abstract warning into a result the client can see.")}</h2>
              </div>
              <p>{t("Connect the appliance, verify the signed engagement, run an automatic preflight and demonstrate approved exposure using the client’s own selected workstation and unimportant test material.")}</p>
            </div>

            <div className="rapid-flow" aria-label={t("Rapid assessment workflow")}>
              <div className="flow-step">
                <span>01</span>
                <div><strong>{t("Connect")}</strong><small>{t("One network cable and private operator control")}</small></div>
              </div>
              <i><Icon name="arrow" /></i>
              <div className="flow-step">
                <span>02</span>
                <div><strong>{t("Preflight")}</strong><small>{t("Scope, interface and automatic cleanup verified")}</small></div>
              </div>
              <i><Icon name="arrow" /></i>
              <div className="flow-step">
                <span>03</span>
                <div><strong>{t("Demonstrate")}</strong><small>{t("Short, bounded client-approved scenario")}</small></div>
              </div>
              <i><Icon name="arrow" /></i>
              <div className="flow-step">
                <span>04</span>
                <div><strong>{t("Prove")}</strong><small>{t("Finding, remediation and repeatable retest")}</small></div>
              </div>
            </div>

            <div className="scenario-grid">
              {scenarios.map((scenario) => (
                <article className="scenario-card" key={scenario.id}>
                  <div className="scenario-topline">
                    <span className="scenario-number">{scenario.number}</span>
                    <span className={`availability availability-${scenario.availability.toLowerCase().replaceAll(' ', '-')}`}>
                      {t(scenario.availability)}
                    </span>
                  </div>
                  <span className="scenario-icon"><Icon name={scenario.icon} size={24} /></span>
                  <h3>{t(scenario.title)}</h3>
                  <p>{t(scenario.description)}</p>
                  <div className="scenario-evidence">
                    <Icon name="document" size={16} />
                    <span>{t(scenario.evidence)}</span>
                  </div>
                  <div className="scenario-footer">
                    <span><Icon name="clock" size={15} /> {t(scenario.duration)}</span>
                    <button type="button" onClick={() => armScenario(scenario)} disabled={activeScenario !== null}>{t("Preview scenario")}<Icon name="arrow" size={16} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-sand" id="appliance">
          <div className="shell appliance-grid">
            <div className="appliance-copy">
              <p className="eyebrow eyebrow-dark">{t("Raspberry Pi 5 prototype")}</p>
              <h2>{t("A field appliance built to arrive ready.")}</h2>
              <p>{t("The appliance prototype uses a Raspberry Pi 5, native Go services and local encrypted storage. Appliance control integration is separate from the monitoring and local-scan features available in the current Android app.")}</p>
              <div className="appliance-list">
                <div><Icon name="check" /><span><strong>{t("Native services")}</strong>{t("No Docker or virtual machine required")}</span></div>
                <div><Icon name="check" /><span><strong>{t("Offline-capable scope")}</strong>{t("Signed engagement rules remain enforceable without cloud access")}</span></div>
                <div><Icon name="check" /><span><strong>{t("Local evidence custody")}</strong>{t("Client documents stay on the appliance by default")}</span></div>
                <div><Icon name="check" /><span><strong>{t("Automatic cleanup")}</strong>{t("Every active scenario has an expiry and stop path")}</span></div>
              </div>
              <a className="text-link" href="https://autosecurelogin.com/#contact">{t("Discuss a pilot assessment")}<Icon name="arrow" size={17} />
              </a>
            </div>

            <div className="appliance-visual" aria-label={t("Raspberry Pi 5 Sentinel appliance architecture")}>
              <div className="appliance-halo" aria-hidden="true" />
              <div className="pi-board">
                <div className="board-header">
                  <span className="board-logo"><Icon name="shield" size={22} /></span>
                  <div><strong>{t("SENTINEL / PI5")}</strong><small>{t("FIELD PROTOTYPE")}</small></div>
                  <i />
                </div>
                <div className="board-chip"><Icon name="chip" size={42} /><span>{t("Native Go agent")}</span></div>
                <div className="board-ports"><i /><i /><i /><i /></div>
                <div className="board-readout">
                  <div><span>{t("Scope")}</span><strong>{t("VERIFIED")}</strong></div>
                  <div><span>{t("Evidence")}</span><strong>{t("LOCAL")}</strong></div>
                  <div><span>{t("Expiry")}</span><strong>{t("ARMED")}</strong></div>
                </div>
              </div>
              <div className="architecture-card architecture-one">
                <Icon name="network" />
                <div><strong>{t("Client LAN")}</strong><small>{t("Discovery + authorized tests")}</small></div>
              </div>
              <div className="architecture-card architecture-two">
                <Icon name="device" />
                <div><strong>{t("Operator console")}</strong><small>{t("Android + installable PWA")}</small></div>
              </div>
              <div className="architecture-card architecture-three">
                <Icon name="globe" />
                <div><strong>{t("ASL backend")}</strong><small>Node.js 26 · Nginx · PostgreSQL</small></div>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-light console-section" id="operator-console">
          <div className="shell">
            <div className="section-heading split-heading">
              <div>
                <p className="eyebrow eyebrow-dark">{t("Operator console preview")}</p>
                <h2>{t("One place to verify scope, run the timer and stop immediately.")}</h2>
              </div>
              <p>{t("This website includes an interface preview and performs no network actions. Live appliance features require separate integration, signed scope and an authorized operating window.")}</p>
            </div>

            <div className="console-window">
              <div className="console-sidebar">
                <div className="console-brand"><Icon name="shield" /><span>Sentinel<strong>{t("Operator")}</strong></span></div>
                <nav aria-label={t("Console preview navigation")}>
                  <button type="button" className="active"><Icon name="grid" />{t("Overview")}</button>
                  <button type="button"><Icon name="network" />{t("Network map")}</button>
                  <button type="button"><Icon name="radar" />{t("Assessments")}</button>
                  <button type="button"><Icon name="document" />{t("Evidence")}</button>
                </nav>
                <div className="console-agent">
                  <span><i />{t("ILLUSTRATIVE APPLIANCE ONLINE")}</span>
                  <strong>sentinel-pi5-01</strong>
                  <small>{t("Prototype console")}</small>
                </div>
              </div>

              <div className="console-main">
                <div className="console-topbar">
                  <div>
                    <span className="mini-label">{t("RAPID ASSESSMENT")}</span>
                    <strong>{t("Authorized scenario console")}</strong>
                  </div>
                  <div className={`api-state api-${t(apiState)}`}>
                    <i /> API {t(apiState)}
                  </div>
                </div>

                <div className="preflight-grid">
                  <div><span>{t("Scope manifest")}</span><strong><Icon name="check" size={15} />{t("Verified")}</strong></div>
                  <div><span>{t("Automatic cleanup")}</span><strong><Icon name="check" size={15} />{t("Enabled")}</strong></div>
                  <div><span>{t("Raw credentials")}</span><strong><Icon name="check" size={15} />{t("Prohibited")}</strong></div>
                  <div><span>{t("Deauthentication")}</span><strong><Icon name="check" size={15} />{t("Disabled")}</strong></div>
                </div>

                <div className={activeScenario ? 'armed-console is-armed' : 'armed-console'} aria-live="polite">
                  <div className="armed-copy">
                    <span className="armed-icon"><Icon name={activeScenario?.icon ?? 'shield'} size={25} /></span>
                    <div>
                      <span className="mini-label">{activeScenario ? t("SCENARIO ARMED") : t("READY FOR PREFLIGHT")}</span>
                      <strong>{activeScenario?.title ? t(activeScenario.title) : t('Select a scenario above to preview the operator flow')}</strong>
                      <small>
                        {activeScenario
                          ? t("Interface preview only. No appliance command or network action is being issued.")
                          : t("Active tests require a paired appliance and signed engagement scope.")}
                      </small>
                    </div>
                  </div>
                  <div className="timer-block">
                    <span>{t("Automatic stop")}</span>
                    <strong>{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</strong>
                  </div>
                  <button className="stop-button" type="button" onClick={stopScenario} disabled={!activeScenario}>
                    <Icon name="stop" size={17} />{t("Emergency stop")}</button>
                  <div className="timer-track"><progress max={100} value={progress} aria-label={t("Scenario time remaining")} /></div>
                </div>

                <div className="console-events">
                  <div className="events-heading"><strong>{t("Live evidence stream")}</strong><span>{t("Preview data")}</span></div>
                  <div className="event-row"><i className="event-good"><Icon name="check" size={14} /></i><span><strong>{t("Scope signature verified")}</strong><small>{t("Scenario permissions and expiry checked locally")}</small></span><time>10:42:01</time></div>
                  <div className="event-row"><i className="event-good"><Icon name="check" size={14} /></i><span><strong>{t("Evidence policy loaded")}</strong><small>{t("Cloud document upload disabled")}</small></span><time>10:42:02</time></div>
                  <div className="event-row"><i className="event-wait"><Icon name="clock" size={14} /></i><span><strong>{activeScenario ? t("Scenario timer running") : t("Awaiting authorized scenario")}</strong><small>{activeScenario ? t("Automatic stop remains active") : t("No active collection or redirection")}</small></span><time>{t("Now")}</time></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-ink" id="safety">
          <div className="shell safety-grid">
            <div className="safety-copy">
              <p className="eyebrow">{t("Designed for authorized use")}</p>
              <h2>{t("The boundary is part of the product.")}</h2>
              <p>{t("Sentinel’s assessment edition is built to demonstrate exposure without becoming an unrestricted interception toolkit. The backend signs the scope; the appliance enforces it again locally.")}</p>
              <a className="button button-ghost button-large" href="https://autosecurelogin.com/security">{t("Read ASL’s security approach")}<Icon name="arrow" size={17} />
              </a>
            </div>
            <div className="safety-controls">
              <article><span><Icon name="lock" /></span><div><strong>{t("Exact scope")}</strong><p>{t("Approved scenario, interface, network, endpoint and expiration are signed before execution.")}</p></div></article>
              <article><span><Icon name="stop" /></span><div><strong>{t("Hard stop paths")}</strong><p>{t("Timer expiry, local emergency stop and cleanup rules terminate active services.")}</p></div></article>
              <article><span><Icon name="eye" /></span><div><strong>{t("Minimal evidence")}</strong><p>{t("Credential simulations retain behavior—not typed passwords, MFA codes, cookies or tokens.")}</p></div></article>
              <article><span><Icon name="document" /></span><div><strong>{t("Client-controlled custody")}</strong><p>{t("Printed test documents remain encrypted on the appliance and are deleted under the selected policy.")}</p></div></article>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="shell cta-panel">
            <div>
              <p className="eyebrow">{t("Sentinel pilot program")}</p>
              <h2>{t("See the devices. Demonstrate the risk. Prove the fix.")}</h2>
              <p>{t("Discuss a Raspberry Pi 5 pilot, a home-network monitor or an authorized rapid assessment for your organization.")}</p>
            </div>
            <div className="cta-actions">
              <a className="button button-primary button-large" href="https://autosecurelogin.com/#contact">{t("Contact Auto Secure Login")}<Icon name="arrow" size={18} />
              </a>
              {installPrompt && (
                <button className="button button-ghost button-large" type="button" onClick={() => void installApp()}>{t("Install Sentinel PWA")}</button>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="shell footer-grid">
          <div className="footer-brand">
            <a className="brand" href="#top">
              <span className="brand-mark" aria-hidden="true"><Icon name="shield" size={23} /><i /></span>
              <span className="brand-copy"><strong>Sentinel</strong><small>{t("by Auto Secure Login")}</small></span>
            </a>
            <p>{t("Network visibility and authorized security validation, beginning with a Raspberry Pi 5 field appliance.")}</p>
          </div>
          <div className="footer-links">
            <div><strong>{t("Help and policies")}</strong><a href={localizedPath("/help-center/index.html")}>{t("Help center")}</a><a href={localizedPath("/privacy.html")}>{t("Privacy policy")}</a><a href={localizedPath("/terms.html")}>{t("Terms of use")}</a></div>
            <div><strong>{t("Platform")}</strong><a href="#monitoring">{t("Monitoring")}</a><a href="#assessment">{t("Rapid assessment")}</a><a href="#appliance">{t("Appliance")}</a></div>
            <div><strong>Auto Secure Login</strong><a href="https://autosecurelogin.com/">{t("Main website")}</a><a href="https://autosecurelogin.com/security">{t("Security")}</a><a href="https://autosecurelogin.com/#contact">{t("Contact")}</a></div>
          </div>
        </div>
        <div className="shell footer-bottom">
          <span>© {new Date().getFullYear()}{t("ASL LLC. Denver, Colorado.")}</span>
          <span>{t("Authorized assessment use only · Prototype availability varies")}</span>
        </div>
      </footer>
    </div>
  );
}
